import { Button, Input, Select } from "@deligoo/ui"
import clsx from "clsx"
import { ChangeEvent, Dispatch, memo, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "@/App"
import { selectCurrentTeamKey } from "@/store/teams"
import { Order } from "@/types"
import { escapeRegex, ORDER_ALLOWED_STATES, PAYMENT_FORMS } from "@/utils"

import cls from "../OrdersListView.module.scss"

type FiltersProps = {
  setFilters: Dispatch<SetStateAction<((order: Order) => boolean)[]>>
}

export const Filters = memo(({ setFilters }: FiltersProps) => {
  const currentTeamUuid = useAppSelector(selectCurrentTeamKey("uuid"))

  const [showFilters, setShowFilters] = useState(false)

  const [searchFilter, setSearchFilter] = useState("")
  const [stateFilter, setStateFilter] = useState<Order["state"] | null>(null)
  const [paymentFormFilter, setPaymentFormFilter] = useState<Order["payment_form"] | null>(null)

  const { t } = useTranslation()

  useEffect(() => {
    setFilters([
      (order) => (stateFilter ? order.state === stateFilter : true),
      (order) => (paymentFormFilter ? order.payment_form === paymentFormFilter : true),

      (order) => {
        if (!searchFilter) return true

        const searchValues = escapeRegex(searchFilter).trim().split(" ")
        const regExps = searchValues.map((searchValue) => new RegExp(searchValue, "i"))

        return regExps.every(
          (regex) =>
            regex.test(order.uid) ||
            regex.test(order.driver?.name || "") ||
            regex.test(order.pickup?.name || "") ||
            regex.test(order.delivery?.phone?.replace(/\s/g, "") || "") ||
            regex.test(order.pickup?.address || "") ||
            regex.test(order.delivery?.address || "") ||
            regex.test(t(`orders:${order.payment_form}`)) ||
            regex.test(t(`orders:${order.state}`))
        )
      },
    ])
  }, [searchFilter, stateFilter, paymentFormFilter, setFilters, t])

  useEffect(() => {
    setSearchFilter("")
  }, [currentTeamUuid])

  return (
    <>
      <div className={cls.nav}>
        <div className={cls.left}>
          <Input
            name="search"
            id="search"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.currentTarget.value)}
            iconRightProps={{ name: "search", color: "primary" }}
            placeholder={t("search_order")}
            plain
            dark
          />
        </div>

        <div className={cls.right}>
          <div className={cls.label}>Typ widoku:</div>

          <Button pill color="primary" iconSide="right" iconProps={{ name: "grid" }} className={cls.navBtn}>
            Lista
          </Button>

          <Button
            pill
            disabled
            color="light"
            iconSide="right"
            iconProps={{ name: "statistics" }}
            className={cls.navBtn}
          >
            Drzewo
          </Button>

          <Button
            onClick={() => setShowFilters((newValue) => !newValue)}
            color={showFilters ? "primary" : "dark"}
            iconProps={{
              name: "filters",
              color: showFilters ? "light" : "primary",
            }}
            raised={showFilters}
          />
        </div>
      </div>

      <div className={clsx(cls.filters, showFilters && cls.active)}>
        <div>
          <label htmlFor="filterByPaymentForm">Forma płatności</label>
          <Select
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setPaymentFormFilter((e.target.value as Order["payment_form"]) || null)
            }
            id="filterByPaymentForm"
            dark
          >
            <option value="">Wszystkie</option>

            {PAYMENT_FORMS.map((paymentForm) => (
              <option value={paymentForm} key={paymentForm}>
                {t(`orders:${paymentForm}`)}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label htmlFor="filterByState">{t("orders_list_headers.state")}</label>
          <Select onChange={(e) => setStateFilter((e.target.value as Order["state"]) || null)} id="filterByState" dark>
            <option value="">Wszystkie</option>

            {ORDER_ALLOWED_STATES.map((state) => (
              <option value={state} key={state}>
                {t(`orders:${state}`)}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </>
  )
})
